import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { ChipSelectSingle } from '@/shared/ui/components/form/ChipSelect';
import { Label, Text, TextInput } from '@/shared/ui';
import { fetchEmployerPrefill } from '../../services/client-field-values';
import type { FieldSuggestion } from '../FieldSuggest/useFieldSuggestions';
import { FieldError } from './FieldError';
import { labelStyle } from './drawer-styles';
import type { ClientFormData } from './schema';
import { EDUCATION_VALUES, GENDER_VALUES, getVocabularyOptions } from './schema.options';
import { ClientSuggestField } from './ClientSuggestField';
import { VocabularySelect } from './VocabularySelect';
import { industryToPrefill } from './employer-prefill';

interface PersonalInfoExtraSectionProps {
  form: UseFormReturn<ClientFormData>;
  collapsed: boolean;
  onToggle: () => void;
  /** The agency whose clients feed the suggestions. */
  agencyId?: string;
}

/** What the employer prefill filled, for the notice under the industry. */
interface PrefillNotice {
  employer: string;
  industry: string;
}

/**
 * Fills the industry from the employer the broker picked, only when the
 * industry is still empty, and reports what it filled.
 *
 * @param form - The client form.
 * @param agencyId - The agency whose clients are read.
 * @param onFilled - Receives what was filled, for the notice.
 * @returns The pick handler for the employer field.
 */
function useEmployerPrefill(
  form: UseFormReturn<ClientFormData>,
  agencyId: string | undefined,
  onFilled: (notice: PrefillNotice) => void,
) {
  return (suggestion: FieldSuggestion) => {
    if (!agencyId) return;
    fetchEmployerPrefill(agencyId, suggestion.value)
      .then((response) => {
        const industry = industryToPrefill(form.getValues('personalInfo.industry'), response);
        if (!industry) return;
        form.setValue('personalInfo.industry', industry, { shouldDirty: true });
        onFilled({ employer: suggestion.value, industry });
      })
      .catch(() => undefined);
  };
}

/**
 * The work fields: profession, job title, employer and industry, each
 * suggesting the agency's own values. Picking an employer fills an empty
 * industry with the one its other clients use most, and says so.
 *
 * @param props - The form and the agency.
 * @returns The four fields and the prefill notice.
 */
function WorkFields({ form, agencyId }: { form: UseFormReturn<ClientFormData>; agencyId?: string }) {
  const { t } = useTranslation('features/clients');
  const [notice, setNotice] = useState<PrefillNotice | null>(null);
  const onEmployerPick = useEmployerPrefill(form, agencyId, setNotice);
  const industry = form.watch('personalInfo.industry');
  const showNotice = notice !== null && industry === notice.industry;

  return (
    <>
      <ClientSuggestField
        form={form}
        name="personalInfo.profession"
        field="profession"
        agencyId={agencyId}
        label={t('drawer.personalExtra.profession')}
        placeholder={t('drawer.personalExtra.professionPlaceholder')}
        maxLength={80}
      />
      <ClientSuggestField
        form={form}
        name="personalInfo.jobTitle"
        field="job_title"
        agencyId={agencyId}
        label={t('drawer.personalExtra.jobTitle')}
        placeholder={t('drawer.personalExtra.jobTitlePlaceholder')}
        maxLength={120}
      />
      <ClientSuggestField
        form={form}
        name="personalInfo.employer"
        field="employer"
        agencyId={agencyId}
        label={t('drawer.personalExtra.employer')}
        placeholder={t('drawer.personalExtra.employerPlaceholder')}
        maxLength={120}
        onPick={onEmployerPick}
      />
      <ClientSuggestField
        form={form}
        name="personalInfo.industry"
        field="industry"
        agencyId={agencyId}
        label={t('drawer.personalExtra.industry')}
        placeholder={t('drawer.personalExtra.industryPlaceholder')}
        maxLength={120}
      />
      {showNotice && (
        <Text
          variant="meta"
          component="span"
          data-testid="client-industry-prefill-notice"
          className="text-(--text-tertiary-soft)"
        >
          {t('drawer.personalExtra.industryPrefilled', { employer: notice.employer, industry: notice.industry })}
        </Text>
      )}
    </>
  );
}

/**
 * Extra personal info: nationality, marital status, gender, education and
 * the work fields.
 */
export const PersonalInfoExtraSection: React.FC<PersonalInfoExtraSectionProps> = ({
  form,
  collapsed,
  onToggle,
  agencyId,
}) => {
  const { t } = useTranslation('features/clients');

  const maritalOptions = [
    { value: 'single', label: t('drawer.personalExtra.maritalOptions.single') },
    { value: 'married', label: t('drawer.personalExtra.maritalOptions.married') },
    { value: 'divorced', label: t('drawer.personalExtra.maritalOptions.divorced') },
    { value: 'widowed', label: t('drawer.personalExtra.maritalOptions.widowed') },
    { value: 'stable_union', label: t('drawer.personalExtra.maritalOptions.stableUnion') },
  ];

  return (
    <FormSection
      title={t('drawer.personalExtra.title')}
      variant="quiet"
      divider
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <Controller
        name="personalInfo.nationality"
        control={form.control}
        render={({ field, fieldState }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>{t('drawer.personalExtra.nationality')}</Label>
            <TextInput
              {...field}
              placeholder={t('drawer.personalExtra.nationalityPlaceholder')}
              maxLength={60}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        name="personalInfo.maritalStatus"
        control={form.control}
        render={({ field }) => (
          <ChipSelectSingle
            label={t('drawer.personalExtra.maritalStatus')}
            options={maritalOptions}
            value={field.value || ''}
            onChange={field.onChange}
          />
        )}
      />

      <div className="grid grid-cols-1 gap-(--space-3) sm:grid-cols-2">
        <VocabularySelect
          form={form}
          name="personalInfo.gender"
          label={t('drawer.personalExtra.gender')}
          placeholder={t('drawer.personalExtra.selectPlaceholder')}
          options={getVocabularyOptions(GENDER_VALUES, 'attributes.gender', t)}
        />
        <VocabularySelect
          form={form}
          name="personalInfo.education"
          label={t('drawer.personalExtra.education')}
          placeholder={t('drawer.personalExtra.selectPlaceholder')}
          options={getVocabularyOptions(EDUCATION_VALUES, 'attributes.education', t)}
        />
      </div>

      <WorkFields form={form} agencyId={agencyId} />
    </FormSection>
  );
};
